import React, { useEffect, useRef, useState, useCallback } from 'react'
import { FormSkeleton } from './PageSkeletons'
import {
  Building2,
  ShoppingCart,
  CheckCircle,
  XCircle,
  Save,
  Loader2,
  Zap,
  Wifi,
  PlugZap,
  Database,
  RefreshCw,
  AlertTriangle,
  Clock,
  ChevronDown,
  ChevronRight,
  History,
} from 'lucide-react'
import { templatesApi, bcApi } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { formatWhen, formatAge } from '../utils/bcTime'
import Toggle from './Toggle'


const SYNC_STATUS_STYLES = {
  success: 'bg-navy-50 text-navy-700 dark:bg-navy-900/20 dark:text-navy-300',
  partial: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
  failed:  'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
  running: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
}

// How a sync was triggered, in words a user recognises rather than the raw
// enum stored in bc_master_data_sync_log.trigger_type.
const TRIGGER_LABELS = {
  manual: 'Manual',
  template_enabled: 'On enabling BC',
  preflight_autosync: 'Auto (first use)',
  scheduled: 'Scheduled',
}


// ─── Toggle row ─────────────────────────────────────────────────

function ToggleRow({ id, label, description, checked, onChange, disabled }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3.5 border-b border-gray-50 dark:border-gray-800 last:border-b-0">
      <div className="flex-1 min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
        )}
      </div>
      <Toggle checked={checked} onChange={() => onChange(!checked)} disabled={disabled} />
    </div>
  )
}


const describeError = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback

function TemplateBCSettings({ templateId, templateName, onSaved }) {
  // Testing the connection, syncing and auto-sync talk to the customer's ERP and are
  // administrator-only on the server. Offering them to everyone else just produced a 403.
  const { user: authUser } = useAuth()
  const isAdmin = authUser?.role === 'admin'
  const [loading, setLoading]       = useState(true)
  const [saving, setSaving]         = useState(false)
  const [saved, setSaved]           = useState(false)
  const [error, setError]           = useState('')

  const [bcEnabled, setBcEnabled]                             = useState(false)
  const [entityType, setEntityType]                           = useState('purchase_order')
  const [bcEntityTypes, setBcEntityTypes]                     = useState([])
  const [requireApproval, setRequireApproval]                 = useState(true)
  const [validateBeforePosting, setValidateBeforePosting]     = useState(true)
  const [blockOnErrors, setBlockOnErrors]                     = useState(true)

  // BC connection state (loaded from backend)
  const [connection, setConnection]   = useState({ connected: false, health: 'unknown', environment: '', company: '' })
  const [testing, setTesting]         = useState(false)
  const [testResult, setTestResult]   = useState(null)

  // Which registered BC connection (tenant + environment + company) this
  // template posts to. Only meaningfully editable when the org has more than
  // one — a single-connection org has nothing to choose between.
  const [bcConnections, setBcConnections]           = useState([])
  const [selectedConnectionId, setSelectedConnectionId] = useState('')
  const [connectionSaving, setConnectionSaving]     = useState(false)

  // Local master data cache — the source every review-time validation reads.
  // Reviewing a document makes no live BC calls once these lists are synced.
  const [masterData, setMasterData]   = useState(null)
  const [mdLoading, setMdLoading]     = useState(false)
  const [syncing, setSyncing]         = useState(false)
  const [syncError, setSyncError]     = useState('')
  const [showHistory, setShowHistory] = useState(false)

  // Polling bookkeeping. Refs (not state) so the poll loop reads current values
  // without re-subscribing, and so cleanup can stop an in-flight loop on
  // unmount without a stale-closure race.
  const pollTimerRef   = useRef(null)
  const pollCountRef   = useRef(0)
  const baselineLogRef = useRef(null)
  const mountedRef     = useRef(true)
  // Set when the 5-minute cap gives up on a sync that is still running server-side, so
  // the "pick up a sync started elsewhere" effect does not immediately restart polling.
  const cappedRef      = useRef(false)

  // 2s cadence, capped at 5 minutes. The cap exists so a sync that dies without
  // releasing its lock can never leave this component polling forever.
  const POLL_MS = 2000
  const POLL_MAX = 150

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
    pollCountRef.current = 0
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false; stopPolling() }
  }, [stopPolling])

  // Auto-sync preference for this template's connection — off by default.
  const [autoSync, setAutoSync]               = useState(false)
  const [autoSyncInterval, setAutoSyncInterval] = useState(24)
  const [autoSyncSaving, setAutoSyncSaving]     = useState(false)
  const [autoSyncError, setAutoSyncError]       = useState('')

  const loadMasterData = useCallback(async () => {
    if (!templateId) return
    setMdLoading(true)
    try {
      const res = await bcApi.getMasterDataStatus(templateId)
      const data = res.data || null
      setMasterData(data)
      if (data?.configured) {
        setAutoSync(Boolean(data.auto_sync))
        setAutoSyncInterval(data.auto_sync_interval_hours || 24)
      }
    } catch {
      setMasterData(null)
    } finally {
      setMdLoading(false)
    }
  }, [templateId])

  // Only meaningful once BC is on for this template — before that there is no
  // connection to scope a cache to.
  useEffect(() => {
    if (bcEnabled) loadMasterData()
  }, [bcEnabled, loadMasterData])

  // Describe a finished run in one line: silence on success, specifics on
  // anything less, because "partial" still means some validation falls back to
  // live BC calls and the user needs to know which lists.
  const reportFinishedSync = useCallback((log) => {
    if (!log) return
    if (log.status === 'failed') {
      let lists = ''
      try {
        const detail = log.detail_json ? JSON.parse(log.detail_json) : null
        if (detail) {
          const failed = Object.keys(detail).filter(k => detail[k]?.status === 'failed')
          if (failed.length) lists = ` (${failed.join(', ')})`
        }
      } catch { /* detail is advisory only — never break the message over it */ }
      setSyncError(`Sync failed${lists}. The cached lists from the last good sync are still in use.`)
    } else if (log.status === 'partial') {
      setSyncError(
        `${log.entities_failed} list${log.entities_failed === 1 ? '' : 's'} could not be synced. ` +
        `Validation falls back to live Business Central calls for those.`
      )
    } else {
      setSyncError('')
    }
  }, [])

  // Poll /master-data/status until the run that was just started finishes.
  //
  // Stopping is keyed on seeing a NEW finished log row rather than simply
  // "syncing === false": right after the POST returns 202 the background thread
  // may not have written its 'running' row yet, so a naive check would see
  // syncing=false on the first poll and declare the sync done immediately.
  const pollUntilSyncFinishes = useCallback(() => {
    stopPolling()
    pollCountRef.current = 0

    const tick = async () => {
      if (!mountedRef.current) return
      pollCountRef.current += 1
      try {
        const res = await bcApi.getMasterDataStatus(templateId)
        if (!mountedRef.current) return
        const data = res.data || null
        setMasterData(data)
        if (data?.configured) {
          setAutoSync(Boolean(data.auto_sync))
          setAutoSyncInterval(data.auto_sync_interval_hours || 24)
        }

        const topLog = data?.recent_syncs?.[0] || null
        const isNewRun = topLog && topLog.sync_log_id !== baselineLogRef.current
        const finished = topLog && topLog.status !== 'running'

        if (!data?.syncing && isNewRun && finished) {
          setSyncing(false)
          stopPolling()
          reportFinishedSync(topLog)
          return
        }
      } catch {
        // A single failed poll is not a failed sync — the run continues
        // server-side. Keep polling; the cap below is the backstop.
      }

      if (pollCountRef.current >= POLL_MAX) {
        cappedRef.current = true
        setSyncing(false)
        stopPolling()
        setSyncError(
          'Still syncing after 5 minutes — it is continuing in the background. ' +
          'Reopen this tab later to see the result.'
        )
        return
      }
      if (mountedRef.current) {
        pollTimerRef.current = setTimeout(tick, POLL_MS)
      }
    }

    pollTimerRef.current = setTimeout(tick, POLL_MS)
  }, [templateId, stopPolling, reportFinishedSync])

  const runSync = async () => {
    cappedRef.current = false
    setSyncing(true)
    setSyncError('')
    baselineLogRef.current = masterData?.recent_syncs?.[0]?.sync_log_id ?? null
    try {
      await bcApi.syncMasterData(templateId)
      pollUntilSyncFinishes()
    } catch (e) {
      if (e?.response?.status === 409) {
        // Someone else's sync (or a double click) already holds the lock. That
        // run is still doing the work we wanted, so follow it rather than
        // reporting a failure.
        setSyncError('A sync was already running — following its progress.')
        pollUntilSyncFinishes()
        return
      }
      setSyncError(e?.response?.data?.error || 'Master data sync could not be started.')
      setSyncing(false)
    }
  }

  // A sync started elsewhere (the scheduler, another admin, or enabling BC on
  // this template) should show live progress here too, not just one this tab
  // started. Picks it up on load and follows it to completion.
  useEffect(() => {
    if (masterData?.syncing && !syncing && !cappedRef.current) {
      setSyncing(true)
      baselineLogRef.current = null
      pollUntilSyncFinishes()
    }
  }, [masterData?.syncing, syncing, pollUntilSyncFinishes])

  // Load connection info, entity types, and every registered BC connection
  useEffect(() => {
    let cancelled = false
    // Independent: one failing call used to leave all three empty with no message.
    Promise.allSettled([
      bcApi.getConnectionInfo(),
      bcApi.getEntityTypes(),
      bcApi.getConnections(),
    ]).then(([connRes, typesRes, connsRes]) => {
      if (cancelled) return
      if (connRes.status === 'fulfilled') setConnection(connRes.value.data || {})
      if (typesRes.status === 'fulfilled') setBcEntityTypes(typesRes.value.data?.entity_types || [])
      if (connsRes.status === 'fulfilled') {
        // Inactive connections stay in the list: a template bound to one must still show
        // it (labelled), not silently display a different connection.
        setBcConnections(connsRes.value.data?.connections || [])
      }
      const failed = [connRes, typesRes, connsRes].filter(r => r.status === 'rejected')
      if (failed.length) {
        setError(describeError(failed[0].reason, 'Some Business Central settings could not be loaded.'))
      }
    })
    return () => { cancelled = true }
  }, [])

  // Switching connections means a different tenant/environment/company, so a
  // different master-data cache scope. The backend already warms it in the
  // background when bc_connection_id changes (see templates_api.update_template
  // → _trigger_first_bc_sync) — this just persists the choice and refreshes
  // the status card so the new scope's (likely unsynced) state is visible.
  const changeConnection = async (newConnectionId) => {
    if (!templateId || !newConnectionId || newConnectionId === selectedConnectionId) return
    const previous = selectedConnectionId
    setSelectedConnectionId(newConnectionId)
    setConnectionSaving(true)
    setError('')
    try {
      await templatesApi.update(templateId, { bc_connection_id: Number(newConnectionId) })
      setTestResult(null)
      await loadMasterData()
      onSaved?.()
    } catch (err) {
      // The server refused (for example an OpCo-owned connection on a shared template):
      // put the selection back, or the page keeps showing a connection that is not saved.
      setSelectedConnectionId(previous)
      setError(describeError(err, 'Failed to switch Business Central connection.'))
    } finally {
      setConnectionSaving(false)
    }
  }

  const saveAutoSync = async (nextAutoSync, nextInterval) => {
    const previous = { on: autoSync, hours: autoSyncInterval }
    setAutoSync(nextAutoSync)
    setAutoSyncInterval(nextInterval)
    setAutoSyncSaving(true)
    setAutoSyncError('')
    try {
      await bcApi.updateMasterDataSettings(templateId, nextAutoSync, nextInterval)
    } catch (err) {
      // Show what the server actually holds, not the value that was refused.
      setAutoSync(previous.on)
      setAutoSyncInterval(previous.hours)
      setAutoSyncError(describeError(err, 'Failed to save auto-sync setting.'))
    } finally {
      setAutoSyncSaving(false)
    }
  }

  // Load settings
  useEffect(() => {
    let active = true
    if (!templateId) return
    setLoading(true)
    setError('')
    templatesApi.get(templateId)
      .then(res => {
        if (!active) return
        const t = res.data || {}
        setBcEnabled(Boolean(t.bc_enabled))
        setEntityType(t.bc_entity_type || 'purchase_order')
        setSelectedConnectionId(t.bc_connection_id ? String(t.bc_connection_id) : '')
        setRequireApproval(t.bc_require_approval !== undefined ? Boolean(t.bc_require_approval) : true)
        setValidateBeforePosting(t.bc_validate_before_posting !== undefined ? Boolean(t.bc_validate_before_posting) : true)
        setBlockOnErrors(t.bc_block_on_errors !== undefined ? Boolean(t.bc_block_on_errors) : true)
      })
      .catch(err => {
        if (!active) return
        setError(err?.response?.data?.error || 'Failed to load settings.')
      })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [templateId])

  // Save helper
  const save = useCallback(async (patch = {}) => {
    if (!templateId) return false
    setSaving(true)
    setSaved(false)
    setError('')
    try {
      const res = await templatesApi.update(templateId, {
        bc_enabled: bcEnabled,
        bc_entity_type: entityType,
        bc_require_approval: requireApproval,
        bc_validate_before_posting: validateBeforePosting,
        bc_block_on_errors: blockOnErrors,
        ...patch,
      })
      const t = res.data || {}
      setBcEnabled(Boolean(t.bc_enabled))
      setEntityType(t.bc_entity_type || entityType)
      setRequireApproval(t.bc_require_approval !== undefined ? Boolean(t.bc_require_approval) : requireApproval)
      setValidateBeforePosting(t.bc_validate_before_posting !== undefined ? Boolean(t.bc_validate_before_posting) : validateBeforePosting)
      setBlockOnErrors(t.bc_block_on_errors !== undefined ? Boolean(t.bc_block_on_errors) : blockOnErrors)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
      onSaved?.()
      return true
    } catch (err) {
      setError(describeError(err, 'Failed to save settings.'))
      return false
    } finally {
      setSaving(false)
    }
  }, [templateId, bcEnabled, entityType, requireApproval, validateBeforePosting, blockOnErrors, onSaved])

  // Toggle BC enabled and immediately save
  const handleToggleBc = async (val) => {
    // When enabling, default entity type to first available if not set
    const nextType = val && !entityType && bcEntityTypes.length > 0
      ? bcEntityTypes[0].bc_entity_type_key
      : entityType
    setBcEnabled(val)
    if (nextType !== entityType) setEntityType(nextType)
    // Saved straight away in BOTH directions. Enabling used to change only local state
    // while the card already read "Active", so leaving the page silently turned it off.
    const ok = await save({ bc_enabled: val, bc_entity_type: nextType })
    if (!ok) setBcEnabled(!val)
  }

  // Changing the document type changes which Business Central fields exist, so mappings
  // made for the old type may no longer apply. Ask first, and tell the parent page once it
  // is saved so its field lists reload for the new type.
  const changeEntityType = (next) => {
    if (next === entityType) return
    const ok = window.confirm(
      'Change the Business Central document type?\n\nField mappings made for the current type may no longer apply to the new one. Review the template\'s field mappings after saving.'
    )
    if (ok) setEntityType(next)
  }

  if (loading) {
    return (
      <FormSkeleton />
    )
  }

  const selectedConnectionRow = bcConnections.find(c => String(c.bc_connection_id) === selectedConnectionId)
  // Active connections, plus the bound one even if it was deactivated, so it shows up
  // (labelled) instead of the select quietly displaying a different connection.
  const visibleConnections = bcConnections.filter(
    c => c.is_active !== false || String(c.bc_connection_id) === selectedConnectionId
  )
  const selectedConnectionInactive = selectedConnectionRow?.is_active === false

  const selectedTypeObj = bcEntityTypes.find(t => t.bc_entity_type_key === entityType)
  const entityLabel = selectedTypeObj?.display_name || 'Unknown Type'

  return (
    <div className="space-y-4 max-w-2xl">

      {/* Feedback */}
      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}
      {saved && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-navy-50 dark:bg-navy-900/20 border border-navy-200 dark:border-navy-800">
          <CheckCircle className="h-4 w-4 text-navy-600 dark:text-navy-400 flex-shrink-0" />
          <p className="text-sm text-navy-700 dark:text-navy-300 font-medium">Settings saved.</p>
        </div>
      )}

      {/* ── Card 1: Enable BC ── */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${bcEnabled ? 'bg-navy-50 dark:bg-navy-900/20' : 'bg-gray-100 dark:bg-gray-800'}`}>
              <Building2 className={`h-5 w-5 transition-colors ${bcEnabled ? 'text-navy-600 dark:text-navy-400' : 'text-gray-400'}`} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Business Central Integration</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {bcEnabled ? 'Active — requests using this template will route to BC.' : 'Enable to send processed documents to Business Central.'}
              </p>
            </div>
          </div>
          <Toggle checked={bcEnabled} onChange={() => handleToggleBc(!bcEnabled)} />
        </div>

        {!bcEnabled && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex flex-col items-center py-6 gap-2">
            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-1">
              <Building2 className="h-6 w-6 text-gray-300 dark:text-gray-600" />
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">BC not enabled for this template</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">Toggle above to configure Business Central posting.</p>
          </div>
        )}
      </div>

      {/* ── Sections only visible when BC is enabled ── */}
      {bcEnabled && (
        <>
          {/* Card 2: Posting Rules */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-navy-50 dark:bg-navy-900/20">
                <Zap className="h-4 w-4 text-navy-600 dark:text-navy-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Posting Rules</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Control how documents are posted to Business Central.</p>
              </div>
            </div>
            <div className="px-5 py-1">
              <ToggleRow
                id="require-approval"
                label="Require Approval Before Posting"
                description="An approver must approve the request before it can be posted to BC."
                checked={requireApproval}
                onChange={setRequireApproval}
              />
              <ToggleRow
                id="validate-before-posting"
                label="Validate Before Posting"
                description="Run field and business rule validation before sending to BC."
                checked={validateBeforePosting}
                onChange={setValidateBeforePosting}
              />
              <ToggleRow
                id="block-on-errors"
                label="Block Posting on Errors"
                description="Prevent posting if any validation errors are found."
                checked={blockOnErrors}
                onChange={setBlockOnErrors}
              />
            </div>
          </div>
          {/* Card 3: Entity Type (dropdown) */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-navy-50 dark:bg-navy-900/20">
                  <ShoppingCart className="h-4 w-4 text-navy-600 dark:text-navy-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">BC Entity Type</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">The document type sent to Business Central.</p>
                </div>
              </div>
              <select
                value={entityType}
                onChange={e => changeEntityType(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 min-w-[200px]"
              >
                {bcEntityTypes.length > 0 ? (
                  bcEntityTypes.map(type => (
                    <option key={type.bc_entity_type_key} value={type.bc_entity_type_key}>
                      {type.display_name}
                    </option>
                  ))
                ) : (
                  <option value={entityType}>{entityLabel}</option>
                )}
              </select>
            </div>
          </div>

          {/* Card 5: BC Connection (Environment + Company + Test) */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-navy-50 dark:bg-navy-900/20">
                <PlugZap className="h-4 w-4 text-navy-600 dark:text-navy-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Connection & Target Company</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Pick the Business Central environment and company this template posts to.</p>
              </div>
              {connection.lastTestedAt && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-navy-50 text-navy-700 dark:bg-navy-900/20 dark:text-navy-300">
                  <Wifi className="h-3 w-3" /> Last tested OK
                </span>
              )}
            </div>
            <div className="px-5 py-4 space-y-4">
              {visibleConnections.length > 1 && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Connection {connectionSaving && <Loader2 className="inline h-3 w-3 animate-spin ml-1" />}
                  </label>
                  <select
                    value={selectedConnectionId}
                    onChange={e => changeConnection(e.target.value)}
                    disabled={connectionSaving}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 disabled:opacity-60"
                  >
                    {!selectedConnectionId && <option value="">— Select a connection —</option>}
                    {visibleConnections.map(c => (
                      <option key={c.bc_connection_id} value={String(c.bc_connection_id)}>
                        {c.connection_label} — {c.bc_environment_name} · {c.company_name}
                        {c.is_active === false ? ' (inactive)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Switching connections resyncs master data in the background for the new company.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Environment</label>
                  <div className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {selectedConnectionRow?.bc_environment_name || connection.environment || '—'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Company</label>
                  <div className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {selectedConnectionRow?.company_name || connection.company || '—'}
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 flex items-center gap-3 flex-wrap">
              <button
                onClick={async () => {
                  setTesting(true); setTestResult(null)
                  try {
                    // Test the connection THIS template posts to. The environment-level
                    // test checked a different one, so a broken bound connection still
                    // read "Connected" and ticked the readiness checklist.
                    let r
                    if (selectedConnectionId) {
                      const res = await bcApi.testConnectionById(selectedConnectionId)
                      const d = res.data || {}
                      r = {
                        connected: Boolean(d.success),
                        company: d.company?.name || selectedConnectionRow?.company_name || '',
                        environment: selectedConnectionRow?.bc_environment_name || '',
                        error: d.success ? '' : (d.message || 'The connection test failed.'),
                      }
                    } else {
                      const res = await bcApi.testConnection()
                      r = res.data
                    }
                    setTestResult(r)
                    if (r.connected) {
                      setConnection(prev => ({
                        ...prev,
                        lastTestedAt: new Date().toISOString(),
                        connected: true,
                        health: 'healthy',
                        company: r.company || prev.company,
                        environment: r.environment || prev.environment,
                      }))
                    }
                  } catch (e) {
                    setTestResult({ connected: false, error: describeError(e, 'The connection test failed.') })
                  }
                  setTesting(false)
                }}
                disabled={testing || !isAdmin}
                title={isAdmin ? undefined : 'Only administrators can test the connection'}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed bg-[var(--token-primary,#1946FA)]"
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
                {testing ? 'Testing…' : 'Test Connection'}
              </button>
              {selectedConnectionInactive && (
                <span className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> This connection is inactive. Documents cannot post until it is reactivated or another is chosen.
                </span>
              )}
              {testResult && testResult.connected && (
                <span className="text-xs text-navy-700 dark:text-navy-300 flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Connected to <b>{testResult.company}</b> · {testResult.environment}
                </span>
              )}
              {testResult && !testResult.connected && (
                <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5">
                  <XCircle className="h-3.5 w-3.5" /> {testResult.error}
                </span>
              )}
            </div>
          </div>

          {/* Card 5b: Local master data cache */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-navy-50 dark:bg-navy-900/20">
                <Database className="h-4 w-4 text-navy-600 dark:text-navy-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Master Data</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Vendors, items, accounts and lookups are kept locally, so reviewing a
                  document validates instantly without calling Business Central.
                </p>
              </div>
              {/* Syncing wins over Synced/Stale: while a run is in flight the
                  other two describe data that is actively being replaced. */}
              {syncing || masterData?.syncing ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                  <Loader2 className="h-3 w-3 animate-spin" /> Syncing
                </span>
              ) : masterData?.configured && masterData.ready && !masterData.staleness?.stale ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-navy-50 text-navy-700 dark:bg-navy-900/20 dark:text-navy-300">
                  <CheckCircle className="h-3 w-3" /> Synced
                </span>
              ) : masterData?.configured && masterData.staleness?.stale ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                  <AlertTriangle className="h-3 w-3" /> Stale
                </span>
              ) : null}
            </div>

            {mdLoading && !masterData ? (
              <div className="px-5 py-6 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : !masterData?.configured ? (
              <div className="px-5 py-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {masterData?.reason
                    || 'Configure a Business Central connection to cache master data for this template.'}
                </p>
              </div>
            ) : (
              <>
                <div className="px-5 py-4 space-y-2.5">
                  {/* Live progress while a run is in flight. Counts come from
                      per-entity sync state the backend writes as each list
                      lands, so this keeps advancing even across a page reload. */}
                  {masterData.running_sync && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/40 px-3 py-2.5">
                      <div className="flex items-center justify-between gap-3 mb-1.5">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Syncing {TRIGGER_LABELS[masterData.running_sync.trigger_type]
                            ? `(${TRIGGER_LABELS[masterData.running_sync.trigger_type].toLowerCase()})`
                            : ''}
                        </span>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {masterData.running_sync.entities_done} of {masterData.running_sync.entities_total} lists
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[var(--token-primary,#1946FA)] transition-all duration-500"
                          style={{ width: `${Math.round(
                            (masterData.running_sync.entities_done /
                              Math.max(1, masterData.running_sync.entities_total)) * 100
                          )}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {!masterData.running_sync && masterData.staleness?.oldest_synced_at && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>
                        Last synced {formatAge(masterData.staleness.oldest_synced_at)}
                        <span className="text-gray-400 dark:text-gray-500">
                          {' '}· {formatWhen(masterData.staleness.oldest_synced_at)}
                        </span>
                        {masterData.staleness.stale
                          && ` — over the ${masterData.stale_after_hours}h freshness window. A vendor or item added since then will not be found.`}
                      </span>
                    </p>
                  )}
                  {!masterData.running_sync && !masterData.staleness?.synced && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Master data has never been synced for this connection.
                    </p>
                  )}
                  {!masterData.ready && !masterData.running_sync && (
                    <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
                      <span>
                        {masterData.unready.length} list
                        {masterData.unready.length === 1 ? '' : 's'} not yet cached
                        ({masterData.unready.join(', ')}).
                        {masterData.strict_cache
                          ? ' Validation is blocked for these until a sync runs.'
                          : ' Validation falls back to live Business Central calls for these.'}
                      </span>
                    </p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 pt-1">
                    {masterData.entities.map(e => {
                      // During a run: ✅ for lists already refreshed by THIS run,
                      // a spinner for the ones still queued — which is the
                      // "vendors ✅, items ✅…" readout, driven by real state
                      // rather than an animation.
                      const inRun = !!masterData.running_sync
                      const doneThisRun = e.synced_in_current_run
                      return (
                        <div
                          key={e.entity_name}
                          className="flex items-center gap-1.5 min-w-0"
                          title={e.last_error
                            || (e.last_synced_at ? `Last synced ${formatWhen(e.last_synced_at)}` : 'Never synced')}
                        >
                          {inRun && !doneThisRun ? (
                            <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin flex-shrink-0" />
                          ) : e.sync_status === 'failed' ? (
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                          ) : e.ready ? (
                            <CheckCircle className="h-3.5 w-3.5 text-navy-500 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                          )}
                          <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{e.entity_name}</span>
                          <span className="text-[11px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                            {e.ready ? e.record_count : '—'}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Auto-sync preference — off by default; a background job that
                    starts talking to Business Central on a timer without anyone
                    asking is not a safe default. */}
                <div className="px-5 py-3.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Toggle
                      checked={autoSync}
                      onChange={() => saveAutoSync(!autoSync, autoSyncInterval)}
                      disabled={autoSyncSaving || !isAdmin}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Auto-sync master data</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Automatically refreshes vendors, items and accounts on a schedule.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={autoSyncInterval}
                      onChange={e => saveAutoSync(autoSync, Number(e.target.value))}
                      disabled={!autoSync || autoSyncSaving || !isAdmin}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 disabled:opacity-50"
                    >
                      <option value={6}>Every 6 hours</option>
                      <option value={12}>Every 12 hours</option>
                      <option value={24}>Every 24 hours</option>
                    </select>
                    {autoSyncSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
                  </div>
                </div>
                {autoSyncError && (
                  <div className="px-5 pb-3 -mt-1">
                    <span className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5" /> {autoSyncError}
                    </span>
                  </div>
                )}

                <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 flex items-center gap-3 flex-wrap">
                  <button
                    onClick={runSync}
                    disabled={syncing || !isAdmin}
                    title={isAdmin ? undefined : 'Only administrators can start a sync'}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed bg-[var(--token-primary,#1946FA)]"
                  >
                    {syncing
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <RefreshCw className="h-4 w-4" />}
                    {syncing ? 'Syncing…' : 'Sync Now'}
                  </button>
                  {syncError && (
                    <span className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5" /> {syncError}
                    </span>
                  )}
                  {!syncError && !syncing && masterData.recent_syncs?.[0] && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Last run: {masterData.recent_syncs[0].status} ·{' '}
                      {masterData.recent_syncs[0].records_written} records ·{' '}
                      {TRIGGER_LABELS[masterData.recent_syncs[0].trigger_type]
                        || masterData.recent_syncs[0].trigger_type}
                    </span>
                  )}
                  {masterData.recent_syncs?.length > 0 && (
                    <button
                      onClick={() => setShowHistory(v => !v)}
                      className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                      aria-expanded={showHistory}
                    >
                      {showHistory
                        ? <ChevronDown className="h-3.5 w-3.5" />
                        : <ChevronRight className="h-3.5 w-3.5" />}
                      <History className="h-3.5 w-3.5" />
                      Sync history
                    </button>
                  )}
                </div>

                {/* Sync history — every attempt, with what triggered it, so an
                    unexpected refresh can be traced to the scheduler vs a person,
                    and a repeated failure is visible as a pattern. */}
                {showHistory && masterData.recent_syncs?.length > 0 && (
                  <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-50 dark:divide-gray-800">
                    {masterData.recent_syncs.map(run => (
                      <div key={run.sync_log_id} className="px-5 py-2.5 flex items-center gap-3 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${
                          SYNC_STATUS_STYLES[run.status] || SYNC_STATUS_STYLES.running
                        }`}>
                          {run.status}
                        </span>
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                          {formatWhen(run.started_at)}
                        </span>
                        <span className="text-[11px] text-gray-400 dark:text-gray-500">
                          {TRIGGER_LABELS[run.trigger_type] || run.trigger_type}
                        </span>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 ml-auto text-right">
                          {run.records_written?.toLocaleString() ?? 0} records
                          {run.entities_failed > 0 && (
                            <span className="text-amber-600 dark:text-amber-400">
                              {' '}· {run.entities_failed} failed
                            </span>
                          )}
                          {run.duration_ms != null && (
                            <span className="text-gray-400 dark:text-gray-500">
                              {' '}· {(run.duration_ms / 1000).toFixed(1)}s
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Card 6: Template Readiness Checklist */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-navy-50 dark:bg-navy-900/20">
                <CheckCircle className="h-4 w-4 text-navy-600 dark:text-navy-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Template Readiness</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">All checks must pass before documents can be posted to BC.</p>
              </div>
            </div>
            <div className="px-5 py-2">
              {(() => {
                const checks = [
                  { label: 'BC Enabled',           ok: bcEnabled },
                  { label: 'Entity Type Selected', ok: !!entityType && bcEntityTypes.length > 0 },
                  { label: 'Environment Selected', ok: !!(selectedConnectionRow?.bc_environment_name || connection.environment) },
                  { label: 'Company Selected',     ok: !!(selectedConnectionRow?.company_name || connection.company) },
                  { label: 'Connection Tested',    ok: !!connection.lastTestedAt },
                  // Validation reads the local cache, so an unsynced connection
                  // means every check falls back to live BC calls.
                  { label: 'Master Data Synced',   ok: !!masterData?.ready },
                ]
                const passed = checks.filter(c => c.ok).length
                const allOk  = passed === checks.length
                return (
                  <>
                    {checks.map(c => (
                      <div key={c.label} className="flex items-center justify-between py-2.5 border-b border-gray-50 dark:border-gray-800 last:border-b-0">
                        <span className={`text-sm ${c.ok ? 'text-gray-800 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`}>{c.label}</span>
                        {c.ok ? <CheckCircle className="h-4 w-4 text-navy-500" /> : <XCircle className="h-4 w-4 text-gray-300 dark:text-gray-600" />}
                      </div>
                    ))}
                    <div className={`mt-3 mb-2 px-3 py-2.5 rounded-lg text-center text-sm font-semibold ${allOk ? 'bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>
                      {allOk ? `✓ Ready for Processing (${passed}/${checks.length})` : `⚠ Template Not Ready (${passed}/${checks.length})`}
                    </div>
                  </>
                )
              })()}
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end pt-1">
            <button
              onClick={() => save()}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed bg-[var(--token-primary,#1946FA)]"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default TemplateBCSettings
